import { useState, useEffect } from 'react';
import { adminAPI } from '@/lib/api';
import { toast } from 'sonner';
import { Trash2, RotateCcw, Search, AlertTriangle, User, FileText, Clock } from 'lucide-react';

export default function AdminTrash() {
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState('');

  const fetch_ = async () => {
    setLoading(true);
    try {
      const r = await adminAPI.trashClients({ page, search, limit: 20 });
      setClients(r.data.clients);
      setTotal(r.data.total);
    } catch { toast.error('Erro ao carregar'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetch_(); }, [page, search]);

  const handleRestore = async (clientId, name) => {
    if (!confirm(`Restaurar cliente "${name}"?`)) return;
    setRestoring(clientId);
    try {
      await adminAPI.restoreClient(clientId);
      toast.success(`${name} restaurado com sucesso`);
      fetch_();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erro'); }
    finally { setRestoring(''); }
  };

  const reasonLabels = {
    cadastro_duplicado: 'Cadastro duplicado',
    cadastro_incorreto: 'Cadastro incorreto',
    solicitacao_interna: 'Solicitacao interna',
    teste: 'Teste',
    outro: 'Outro',
  };

  return (
    <div className="space-y-5" data-testid="admin-trash">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-semibold flex items-center gap-2"><Trash2 size={22} className="text-white/40"/> Lixeira</h1>
          <p className="text-sm text-white/40">{total} cliente(s) excluido(s)</p>
        </div>
      </div>

      <div className="relative">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30"/>
        <input value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} placeholder="Buscar cliente excluido..."
          className="w-full bg-black/20 border border-white/10 text-white text-sm placeholder:text-white/30 rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-[rgba(99,203,182,0.3)]"/>
      </div>

      <div className="glass-card !p-0 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-10"><div className="rn-spinner" style={{width:24,height:24}}/></div>
        ) : clients.length === 0 ? (
          <div className="text-center py-12">
            <Trash2 size={36} className="mx-auto text-white/10 mb-2"/>
            <p className="text-sm text-white/30">Lixeira vazia</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px] text-sm">
              <thead>
                <tr className="border-b border-white/10 bg-white/[0.02]">
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">CLIENTE</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">CPF</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">MOTIVO</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">EXCLUIDO POR</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">DATA</th>
                  <th className="text-left text-[10px] text-white/40 font-medium py-2.5 px-3">VINCULOS</th>
                  <th className="text-center text-[10px] text-white/40 font-medium py-2.5 px-3">ACAO</th>
                </tr>
              </thead>
              <tbody>
                {clients.map(c => (
                  <tr key={c.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                    <td className="py-2.5 px-3">
                      <p className="text-xs font-medium text-white/70">{c.name}</p>
                      <p className="text-[9px] text-white/30">{c.email}</p>
                    </td>
                    <td className="py-2.5 px-3"><span className="text-xs font-mono text-white/50">{c.cpf || '—'}</span></td>
                    <td className="py-2.5 px-3">
                      <span className="badge-neutral text-[9px]">{reasonLabels[c.deletionReason] || c.deletionReason}</span>
                      {c.deletionReasonDetail && <p className="text-[9px] text-white/25 mt-0.5">{c.deletionReasonDetail}</p>}
                    </td>
                    <td className="py-2.5 px-3 text-xs text-white/40">{c.deletedByName || '—'}</td>
                    <td className="py-2.5 px-3 text-xs text-white/35">{c.deletedAt ? new Date(c.deletedAt).toLocaleDateString('pt-BR') : '—'}</td>
                    <td className="py-2.5 px-3">
                      <div className="flex gap-1">
                        {c.dependencies?.documents > 0 && <span className="badge-neutral text-[8px]">{c.dependencies.documents} docs</span>}
                        {c.dependencies?.terms > 0 && <span className="badge-neutral text-[8px]">{c.dependencies.terms} termos</span>}
                      </div>
                    </td>
                    <td className="py-2.5 px-3 text-center">
                      <button onClick={() => handleRestore(c.id, c.name)} disabled={restoring === c.id}
                        className="text-[9px] px-3 py-1.5 rounded-lg flex items-center gap-1 mx-auto" style={{color:'var(--rn-green)', background:'rgba(99,203,182,0.08)', border:'1px solid rgba(99,203,182,0.2)'}}>
                        {restoring === c.id ? <span className="rn-spinner" style={{width:10,height:10}}/> : <><RotateCcw size={11}/> Restaurar</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
